import { useJobStore } from "@/dentlab/src/store/Jobs";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { PractitionerEntityType } from "@/lib/supabase/supabaseTypes";
import { CustomSelectShadcn } from "@/dentlab/src/components/custom-select-shadcn.component";
import { supabase } from "@/lib/supabase";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";

const formatPractitionerLabel = (p: PractitionerEntityType): string => {
	const parts = [p.title, p.first_name, p.last_name].filter(Boolean);
	return parts.join(" ") || "Unbenannt";
};

export const JobPractitionerSelect: React.FC<{
	inputHeight?: number;
	validationError?: string | null;
}> = ({ inputHeight, validationError }) => {
	const { job, initializePractitionerFormFields, clearFieldProblems } =
		useJobStore((state) => ({
			job: state.job,
			initializePractitionerFormFields:
				state.initializePractitionerFormFields,
			clearFieldProblems: state.clearFieldProblems,
		}));

	const { practitionersLookup } = useCentralStore((state) => ({
		practitionersLookup: state.practitionersLookup,
	}));

	const clientPractitioners = Object.values(practitionersLookup).filter(
		(p) => p.client_id === job?.client_id
	);

	const options = [
		{ value: "", label: "Kein Behandler" },
		...clientPractitioners.map((p) => ({
			value: String(p.id),
			label: formatPractitionerLabel(p),
		})),
	];

	const handleChange = async (value: string | number | null) => {
		if (!job?.id) return;
		clearFieldProblems(["practitioner_select"]);

		if (!value) {
			await supabase
				.from(SupabaseTableEnum.JOBS)
				.update({ practitioner_id: null })
				.eq("id", job.id);
			useJobStore.setState((state) => ({
				...state,
				job: state.job ? { ...state.job, practitioner_id: null } : null,
			}));
			return;
		}

		const practitionerId = Number(value);
		const practitioner = practitionersLookup[practitionerId];
		if (!practitioner) return;

		await supabase
			.from(SupabaseTableEnum.JOBS)
			.update({ practitioner_id: practitionerId })
			.eq("id", job.id);
		useJobStore.setState((state) => ({
			...state,
			job: state.job
				? { ...state.job, practitioner_id: practitionerId }
				: null,
		}));
		initializePractitionerFormFields(practitioner);
	};

	const densitySize = inputHeight
		? inputHeight <= 32
			? ("sm" as const)
			: inputHeight <= 40
				? ("md" as const)
				: undefined
		: undefined;

	return (
		<CustomSelectShadcn
			options={options}
			inputLabel="Behandlerauswahl"
			value={job?.practitioner_id ? String(job.practitioner_id) : ""}
			onChange={handleChange}
			densitySize={densitySize}
			inputHeight={inputHeight}
			aria-label="Behandler auswählen"
			data-testid="practitioner-select"
			validationError={validationError}
		/>
	);
};
